import { SubscriptionInfo, UserSettings as slUserSettings, MonthFilterT as slMonthFilterT } from 'shovel-lib/types';
import { Substeps } from '@components/common/onboardingBar/helpers';
import { PlanningPreferences } from '@utils/taskPlanning';
import { DarkHours, ThemePreference } from '@utils/theme/themePreference';
import { BumpersMode } from '@utils/settingsUtils';

// TEMP (todo: make shovel-lib a package with monorepo and come back to this)
export type MonthFilterT = Omit<slMonthFilterT, 'studyTimeEvents' | 'courseEvents'> & {
  courseCorrelationIds: string[];
  exams?: boolean;
};
export type UserSettings = Omit<slUserSettings, 'monthFilter'> & {
  monthFilter: MonthFilterT;
  courseEventStyle: {
    style: calendarStylingType;
    sidebar: boolean;
  };
  activityEventStyle: {
    style: calendarStylingType;
    sidebar: boolean;
  };
  googleEventStyle: {
    style: calendarStylingType;
    sidebar: boolean;
  };
  plannedTaskEventStyle: {
    style: calendarStylingType;
    sidebar: boolean;
  };
  showCompletedTasks: {
    calendarHeader: boolean;
    flags: boolean;
  };
  planningPreferences?: Partial<PlanningPreferences>;
  /** The ✓ disc on past planned blocks whose time isn't marked used; read through `showsMarkUsedButtons`, on unless turned off. */
  showMarkUsedButtons?: boolean;
  /** Dropping a course or activity opens the weekly repeat options; read through `repeatsDroppedCommitments`, on unless turned off. */
  repeatDroppedCommitments?: boolean;
  /** Bumpers: a dragged session bumps the sessions it lands on down; read through `usesBumpers`, on unless turned off. */
  bumpers?: boolean;
  /** What happens to the sessions a drag passes; read through `bumpersMode`, Jump unless chosen. */
  bumpersMode?: BumpersMode;
  /** The calendar has shown what Bumpers is, once (BumpersIntro); read through `bumpersIntroDue`. */
  bumpersIntroSeen?: boolean;
  // What the timer's number shows; the last choice is kept for the next session (utils/timer.ts savedTimerMode).
  timerMode?: 'countdown' | 'stopwatch';
  /** The timer's chime when time's up; read through `playsTimerChime`, on unless turned off. */
  timerChime?: boolean;
  /** The timer's desktop notice when time's up; read through `showsTimerNotice`, on unless turned off. */
  timerNotice?: boolean;
  /** What ▶ on a late session does by default; read through `savedLateStart`, bumping the day down unless chosen. */
  lateStart?: 'bump' | 'shortenThis' | 'shortenNext' | 'untilSession';
  /** The study streak at the top of the calendar; read through `showsStudyStreak`, which falls back to this browser's choice. */
  showStudyStreak?: boolean;
  /** The streak's last days as bars beside the flame; read through `showsStudyStreakDays`, on unless turned off. */
  showStudyStreakDays?: boolean;
  /** "to study" under free study time on the calendar. Missing on accounts from before the setting: off for them. */
  showToStudyLabel?: boolean;
  /** Courses whose "Estimate tasks" box the student closed; read through `dismissedEstimateCourses`. */
  dismissedEstimateCourseIds?: number[];
  /** Light, dark, Auto or set hours, on the account since 2026-10-08; before, each browser kept its own. */
  theme?: ThemePreference;
  /** When set hours go dark and back to light, in minutes after midnight on the device's clock. */
  darkHours?: DarkHours;
  /** The colour scheme: a preset's name, or a custom colour's hex. Kept short, as the settings have a size limit. */
  colorScheme?: string;
  /** The soft background behind the app in light mode: a `softBackgrounds` key; Soft white when missing. */
  background?: string;
  /** Set once White was moved to Soft white (2026-10-11), or a background was picked after that: see `softWhiteSwitch`. */
  softWhiteSwitched?: boolean;
  /** The dark mode's background: a `darkBackgrounds` key; Ink when missing. */
  darkBackground?: string;
  /** Free study time's own colour, as #RRGGBB; missing, it follows the accent (`freeTimeColors`). */
  freeTimeColor?: string;
  /** The Task List's Sort: 'hand' for the student's own dragged order; missing, by due date. See `sortsTaskListByHand`. */
  taskListSort?: 'hand';
  /**
   * A column the Task List is sorted by instead ("priority", or "priority:desc"), from the Sort by menu or its header;
   * missing, it shows `taskListSort`'s order. See `taskListColumnSort`.
   */
  taskListColumnSort?: string;
  /** How a new task's Start ahead is set: 'time' from its time needed; 'days' or missing, `daysAhead` days. */
  startAheadMode?: 'time' | 'days';
};

export const initialMonthFilterState: MonthFilterT = {
  tasks: true,
  courseCorrelationIds: [],
  activityCorrelationIds: [],
  customEvents: false,
  /*TODO: remove*/
  googleEvents: false
};

export const resetMonthFilterState: MonthFilterT = {
  tasks: false,
  courseCorrelationIds: [],
  activityCorrelationIds: [],
  customEvents: false,
  /*TODO: remove*/
  googleEvents: false
};

export type BadgesInfo = {
  badges: string[];
  steps: string[];
  substeps: Substeps[];
  completed: boolean;
};

export type SettingsState = {
  subscription?: SubscriptionInfo;
  userSettings?: UserSettings;
  helpUsDialog: {
    open: boolean;
    confirmAction: any;
    options: any;
    message?: string;
    loadingAction?: any;
  };
  badgesInfo: BadgesInfo | null;
  badgesObviousMike: boolean;
  showOnboardingBar: boolean;
  stepDialogInfo: {
    open: boolean;
    step?: string;
  };
  personalizationDialog: {
    open: boolean;
    callback?: any;
  };
};

export enum calendarStylingType {
  WHITE = 'WHITE',
  GRAY = 'GRAY',
  LIGHT = 'LIGHT',
  FULL = 'FULL'
}
